import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Plane, Mail, Phone, MapPin, Facebook, Instagram, CheckCircle2 } from 'lucide-react';
import DestinationCard from '../components/DestinationCard';
import PackageCard from '../components/PackageCard';
import Modal from '../components/Modal';
import QuoteRequestModal from '../components/QuoteRequestModal';
import { GET_AGENCY_PAGE_DATA, GET_AGENCY_PAGE_DATA_BY_ID } from '../graphql/queries/agency';
import type { AgencyPageData } from '../types/agency';
import { getImageUrl } from '../utils/imageUrl';
import { VISITOR_SEND_CONTACT_MUTATION } from '../graphql/mutations/agency';
import ClientReservationSection from '../components/ClientReservationSection';

// Components
const navLinks = [
  { href: '#packages', label: 'Nos séjours' },
  { href: '#destinations', label: 'Destinations' },
  { href: '#about', label: 'À propos' },
  { href: '#prestations', label: 'Nos prestations' },
  { href: '#ma-reservation', label: 'Ma réservation' },
  { href: '#contact', label: 'Contact' },
];

const Header = ({ 
  agencyName, 
  agencyLogo,
  primaryColor,
  secondaryColor,
  textColor = '#FFFFFF',
  onDevisClick
}: { 
  agencyName: string; 
  agencyLogo: string | null;
  primaryColor: string;
  secondaryColor: string;
  textColor?: string;
  onDevisClick?: () => void;
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const logoContent = (
    <>
      {agencyLogo ? (
        <img 
          src={agencyLogo} 
          alt={agencyName}
          className="h-12 w-12 md:h-16 md:w-16 object-cover"
        />
      ) : (
        <Plane className="h-12 w-12 md:h-16 md:w-16" style={{ color: textColor }} />
      )}
    </>
  );
  return (
  <header 
    className="fixed top-0 left-0 right-0 z-50"
    style={{ 
      backgroundColor: secondaryColor
    }}
  >
    <div className="container mx-auto px-4">
      <div className="flex items-center justify-between h-16 md:h-20">
        <div>
          <button
            type="button"
            className="block hover:opacity-90 transition cursor-pointer"
            onClick={scrollToTop}
          >
            {logoContent}
          </button>
        </div>
        <nav className="hidden md:flex space-x-8">
          {navLinks.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className="hover:opacity-100 transition-opacity"
              style={{ color: textColor, opacity: 0.8 }}
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="md:hidden p-2 rounded-lg hover:opacity-80 transition-opacity"
            style={{ color: textColor }}
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
          <button 
            type="button"
            onClick={onDevisClick}
            className="px-4 py-2 md:px-6 md:py-2 rounded-full text-sm md:text-base transition-colors"
            style={{ 
              backgroundColor: primaryColor,
              color: secondaryColor
            }}
          >
            Devis
          </button>
        </div>
      </div>
      {/* Menu mobile */}
      {mobileMenuOpen && (
        <nav
          className="md:hidden py-4 border-t border-white/20"
          style={{ color: textColor }}
        >
          <div className="flex flex-col gap-2">
            {navLinks.map(({ href, label }) => (
              <a
                key={href}
                href={href}
                onClick={() => setMobileMenuOpen(false)}
                className="py-2 px-2 hover:opacity-100 transition-opacity rounded-lg"
                style={{ opacity: 0.9 }}
              >
                {label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </div>
  </header>
  );
};

const Hero = ({ 
  agencyName, 
  primaryColor,
  firstTitle,
  secondTitle,
  imageCover,
  textColor = '#FFFFFF'
}: { 
  agencyName: string; 
  primaryColor: string;
  firstTitle: string | null;
  secondTitle: string | null;
  imageCover: string | null;
  textColor?: string;
}) => (
  <section className="relative h-screen">
    <div className="absolute inset-0">
      <img
        src={imageCover || "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1"}
        alt="Beautiful landscape"
        className="w-full h-full object-cover opacity-90"
      />
      <div className="absolute inset-0 bg-black/50" />
    </div>
    <div className="relative container mx-auto px-4 h-full flex items-center">
      <div className="max-w-2xl" style={{ color: textColor }}>
        <h1 className="text-5xl md:text-6xl font-bold mb-6">
          {firstTitle || `Bienvenue chez ${agencyName}`}
        </h1>
        <p className="text-xl mb-8">
          {secondTitle || "Des voyages inoubliables et des expériences uniques vous attendent."}
        </p>
        <div className="flex space-x-4">
          <a 
            href="#packages"
            className="text-white px-8 py-3 rounded-full transition-colors"
            style={{ 
              backgroundColor: primaryColor,
              '&:hover': {
                backgroundColor: primaryColor + 'dd'
              }
            }}
          >
            Nos séjours
          </a>
          <a 
            href="#about"
            className="bg-white text-gray-900 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors"
          >
            À propos de nous
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Contact = ({ 
  primaryColor,
  agencyId,
  email,
  phone,
  address 
}: { 
  primaryColor: string;
  agencyId: number;
  email: string | null;
  phone: string | null;
  address: string | null;
}) => {

  const [name, setName] = useState("");
  const [visitorEmail, setVisitorEmail] = useState("");
  const [message, setMessage] = useState("");

 
  const [sendContact, { loading }] = useMutation(VISITOR_SEND_CONTACT_MUTATION);

  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await sendContact({
        variables: {
          name,
          email: visitorEmail,
          message,
          agencyId
        }
      });

      alert("Message envoyé ✅");

      // reset form
      setName("");
      setVisitorEmail("");
      setMessage("");

    } catch (error) {
      console.error(error);
      alert("Erreur lors de l'envoi ❌");
    }
  };

 return (
  <section id="contact" className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4">Contactez-nous</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Notre équipe d'experts en voyage est là pour vous aider à planifier
          votre prochaine aventure.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
          {phone && (
          <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: `${primaryColor}20` }}>
                <Phone className="h-6 w-6" style={{ color: primaryColor }} />
            </div>
            <div>
              <h3 className="font-semibold">Téléphone</h3>
                <p className="text-gray-600">{phone}</p>
              </div>
            </div>
          )}
          {email && (
          <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: `${primaryColor}20` }}>
                <Mail className="h-6 w-6" style={{ color: primaryColor }} />
            </div>
            <div>
              <h3 className="font-semibold">Email</h3>
                <p className="text-gray-600">{email}</p>
              </div>
            </div>
          )}
          {address && (
          <div className="flex items-center space-x-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: `${primaryColor}20` }}>
                <MapPin className="h-6 w-6" style={{ color: primaryColor }} />
            </div>
            <div>
              <h3 className="font-semibold">Adresse</h3>
                <p className="text-gray-600">{address}</p>
              </div>
            </div>
          )}
        </div>
        
         {/* FORM pour envoie message  */}
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Votre nom"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
              style={{ '&:focus': { '--tw-ring-color': primaryColor } }}
            />
          </div>
          <div>
            <input
              type="email"
              value={visitorEmail}
              onChange={(e) => setVisitorEmail(e.target.value)}
              placeholder="Votre email"
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
              style={{ '&:focus': { '--tw-ring-color': primaryColor } }}
            />
          </div>
          <div>
            <textarea
              placeholder="Votre message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent"
              style={{ '&:focus': { '--tw-ring-color': primaryColor } }}
            ></textarea>
          </div>
          <button  
            className="w-full text-white px-6 py-3 rounded-lg transition-colors"
            disabled={loading}
            style={{ 
              backgroundColor: primaryColor,
              '&:hover': {
                backgroundColor: primaryColor + 'dd'
              }
            }}
          >
           {loading ? "Envoi..." : "Envoyer"}
          </button>
        </form>
      </div>
    </div>
  </section>
  );
};

const Prestations = ({ 
  prestations,
  primaryColor 
}: { 
  prestations: string[];
  primaryColor: string;
}) => {
  const list = Array.isArray(prestations) ? prestations : [];
  if (list.length === 0) return null;
  return (
    <section id="prestations" className="py-24 overflow-hidden relative bg-gray-50/80">
      <div 
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ 
          backgroundImage: `radial-gradient(circle at 2px 2px, ${primaryColor} 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}
      />
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <span 
            className="inline-block text-sm font-semibold tracking-wider uppercase mb-3"
            style={{ color: primaryColor }}
          >
            Ce que nous offrons
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            Nos prestations
          </h2>
          <div 
            className="w-24 h-1 mx-auto mb-6 rounded-full"
            style={{ backgroundColor: primaryColor }}
          />
          <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            Découvrez les services que nous proposons pour rendre votre voyage inoubliable.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {list.map((presta, index) => (
            <div
              key={index}
              className="group relative p-8 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-transparent transition-all duration-300 overflow-hidden"
              style={{
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)'
              }}
            >
              <div 
                className="absolute top-0 right-0 w-32 h-32 rounded-full -translate-y-1/2 translate-x-1/2 opacity-10 group-hover:opacity-20 transition-opacity duration-300"
                style={{ backgroundColor: primaryColor }}
              />
              <div className="relative flex items-start gap-4">
                <div 
                  className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}18` }}
                >
                  <CheckCircle2 
                    className="w-6 h-6" 
                    style={{ color: primaryColor }} 
                    strokeWidth={2.5}
                  />
                </div>
                <div className="pt-0.5">
                  <p className="text-gray-800 font-medium leading-relaxed text-base">
                    {presta}
                  </p>
                </div>
              </div>
              <div 
                className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                style={{ backgroundColor: primaryColor }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Bouton WhatsApp flottant : numéro au format international (chiffres uniquement pour wa.me)
const WhatsAppButton = ({ phone }: { phone: string | null }) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (!digits.length) return null;
  const waUrl = `https://wa.me/${digits}`;
  return (
    <a
      href={waUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-[9999] flex items-center justify-center w-14 h-14 rounded-full shadow-lg hover:scale-105 transition-transform text-white"
      style={{ backgroundColor: '#25D366' }}
      title="Contacter sur WhatsApp"
      aria-label="Contacter sur WhatsApp"
    >
      <svg viewBox="0 0 24 24" className="w-8 h-8" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Ajout du composant TikTok personnalisé
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 015.9 5.82s-.51.5 0 0zm-1.84-1.66a4.278 4.278 0 01-8.76 0s-.51.5 0 0zm7.38 3.11C23.16 9.39 23 15.55 23 15.55a9.46 9.46 0 01-9.46 9.46c-5.22 0-9.46-4.24-9.46-9.46 0-5.22 4.24-9.46 9.46-9.46.47 0 .94.03 1.4.09v3.37c-.47-.07-.94-.1-1.4-.1-3.39 0-6.14 2.75-6.14 6.14a6.14 6.14 0 006.14 6.14c3.39 0 6.14-2.75 6.14-6.14v-9.3c1.13 1.06 2.64 1.71 4.3 1.71v-3.37c-1.66 0-3.17-.65-4.3-1.71z"/>
  </svg>
);

// Définir une interface pour les posts Instagram
interface InstagramPost {
  id: string;
  mediaUrl: string;
  permalink: string;
  caption: string;
  timestamp: string;
  mediaType: string;
  thumbnail?: string;
}

const SocialLinks = ({ 
  facebook,
  instagram,
  tiktok,
  primaryColor
}: { 
  facebook: string | null;
  instagram: string | null;
  tiktok: string | null;
  primaryColor: string;
}) => (
  <section className="py-12 bg-gray-50">
    <div className="container mx-auto px-4">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold mb-4">Suivez-nous</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Restez connectés avec nous sur les réseaux sociaux
        </p>
      </div>
      <div className="flex justify-center items-center gap-8">
        {facebook && (
          <a 
            href={facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="p-4 rounded-full hover:scale-110 transition-all duration-300 shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <Facebook className="h-8 w-8 text-white" />
          </a>
        )}
        {instagram && (
          <a 
            href={instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="p-4 rounded-full hover:scale-110 transition-all duration-300 shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <Instagram className="h-8 w-8 text-white" />
          </a>
        )}
        {tiktok && (
          <a 
            href={tiktok}
            target="_blank"
            rel="noopener noreferrer"
            className="p-4 rounded-full hover:scale-110 transition-all duration-300 shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <TikTokIcon className="h-8 w-8 text-white" />
          </a>
        )}
      </div>
    </div>
  </section>
);

const PopularDestinations = ({ 
  destinations,
  primaryColor,
  onDevisClick
}: { 
  destinations: AgencyPageData['popularDestinations'];
  primaryColor: string;
  onDevisClick?: () => void;
}) => (
  <section id="destinations" className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4">Destinations Populaires</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Découvrez nos destinations les plus prisées et laissez-vous séduire par 
          ces lieux d'exception qui font rêver nos voyageurs.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {destinations.map((destination) => (
          <DestinationCard 
            key={destination.id}
            {...destination}
            primaryColor={primaryColor}
            onDevisClick={onDevisClick}
          />
        ))}
      </div>
    </div>
  </section>
);

const PackageSection = ({ 
  packages,
  primaryColor 
}: { 
  packages: AgencyPageData['packages'];
  primaryColor: string;
}) => (
  <section id="packages" className="py-20 bg-gray-50">
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold mb-4">Nos séjours</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Découvrez nos packages soigneusement élaborés pour vous offrir 
          des expériences de voyage inoubliables.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {packages
          .filter(pkg => pkg.type === 'B2C')
          .map((pkg) => (
            <PackageCard 
              key={pkg.id}
              package={pkg}
              primaryColor={primaryColor}
            />
          ))
        }
      </div>
    </div>
  </section>
);

const About = ({ 
  description,
  aboutImage,
  yearsExperience,
  expertise,
  satisfiedTravelers,
  primaryColor,
  secondaryColor
}: { 
  description: string | null;
  aboutImage: string | null;
  yearsExperience: number;
  expertise: string | null;
  satisfiedTravelers: number;
  primaryColor: string;
  secondaryColor: string;
}) => (
  <section id="about" className="py-20 bg-white">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div 
            className="absolute inset-0 transform translate-x-4 translate-y-4 rounded-lg"
            style={{ backgroundColor: `${primaryColor}20` }}
          />
          <img
            src={aboutImage || "https://images.unsplash.com/photo-1464746133101-a2c3f88e0dd9"}
            alt="À propos de nous"
            className="relative w-full h-[500px] object-cover rounded-lg shadow-xl"
          />
        </div>
        <div>
          <h2 className="text-4xl font-bold mb-6">À propos de nous</h2>
          <div 
            className="w-20 h-1 mb-8 rounded"
            style={{ backgroundColor: primaryColor }}
          />
          <div className="prose prose-lg mb-8">
            <p className="text-gray-600">
              {description || "Bienvenue dans notre agence de voyage. Nous sommes spécialisés dans l'organisation de voyages sur mesure."}
            </p>
          </div>
          {/* Stats en ligne */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: `${primaryColor}10` }}
            >
              <h3 className="text-xl font-bold mb-2">Années d'expérience</h3>
              <p className="text-3xl font-bold" style={{ color: primaryColor }}>
                {yearsExperience}+
              </p>
              <p className="text-sm text-gray-600 mt-1">
                ans d'expertise en voyage
              </p>
            </div>
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: `${primaryColor}10` }}
            >
              <h3 className="text-xl font-bold mb-2">Voyageurs satisfaits</h3>
              <p className="text-3xl font-bold" style={{ color: primaryColor }}>
                {satisfiedTravelers}+
              </p>
              <p className="text-sm text-gray-600 mt-1">
                clients nous ont fait confiance
              </p>
            </div>
          </div>
          {/* Expertise en bas */}
          <div 
            className="p-4 rounded-lg w-full"
            style={{ backgroundColor: `${secondaryColor}10` }}
          >
            <h3 className="text-xl font-bold mb-2">Notre Expertise</h3>
            <p className="text-gray-600">
              {expertise || "Spécialistes des voyages sur mesure, circuits culturels et séjours de groupe."}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}

export default function AgencyPage() {
  const { id: idParam } = useParams<{ id?: string }>();
  const [showDevisModal, setShowDevisModal] = useState(false);
  const currentHost = window.location.hostname;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [idParam]);
  const isLocalhost = currentHost === 'localhost' || currentHost.includes('127.0.0.1');
  const isNumericId = idParam != null && /^\d+$/.test(idParam);

  const getSubdomain = (hostname: string): string => {
    const parts = hostname.split('.');
    if (hostname === 'localhost' || hostname.includes('127.0.0.1') || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return parts[0] || '';
    }
    if (parts[0] === 'www' && parts.length > 2) {
      return parts[1] || '';
    }
    return parts[0] || '';
  };
  const subdomain = getSubdomain(currentHost);

  const { data: dataById, loading: loadingById, error: errorById } = useQuery<{ agencyPageDataById: AgencyPageData }>(
    GET_AGENCY_PAGE_DATA_BY_ID,
    {
      variables: { id: parseInt(idParam!, 10) },
      skip: !isNumericId,
      fetchPolicy: 'network-only',
      onError: (err) => console.error('Apollo (by id):', err.message),
    }
  );

  const { data: dataBySubdomain, loading: loadingBySubdomain, error: errorBySubdomain } = useQuery<{ agencyPageData: AgencyPageData }>(
    GET_AGENCY_PAGE_DATA,
    {
      variables: { subdomain },
      skip: isNumericId,
      fetchPolicy: 'network-only',
      onError: (error) => {
        console.error('Erreur détaillée Apollo:', {
          message: error.message,
          graphQLErrors: error.graphQLErrors,
          networkError: error.networkError,
        });
      },
    }
  );

  const loading = loadingById || loadingBySubdomain;
  const error = errorById || errorBySubdomain;
  const agency = dataById?.agencyPageDataById ?? dataBySubdomain?.agencyPageData;

  useEffect(() => {
    if (agency && !isLocalhost) {
      const appDomain = import.meta.env.VITE_APP_DOMAIN || 'ziyara.site';
      const expectedHost = `${agency.subdomain}.${appDomain}`;
      if (currentHost !== expectedHost) {
        window.location.href = `https://${expectedHost}`;
      }
    }
  }, [agency, currentHost, isLocalhost]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-red-600">
          <p>Erreur: {error.message}</p>
          {error.graphQLErrors?.map((err, index) => (
            <p key={index} className="text-sm mt-2">
              {err.message}
            </p>
          ))}
        </div>
      </div>
    );
  }

  const primaryColor = agency?.theme?.primaryColor || '#f97316';
  const secondaryColor = agency?.theme?.secondaryColor || '#ea580c';
  const headerTextColor = agency?.theme?.accentColor || agency?.theme?.textColor || primaryColor;
  const heroTextColor = agency?.theme?.heroTextColor ?? '#FFFFFF';

  return (
    <div className="min-h-screen">
      <Header 
        agencyName={agency?.name || 'Agence'} 
        agencyLogo={agency?.logo ? getImageUrl(agency.logo) : null}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        textColor={headerTextColor}
        onDevisClick={() => setShowDevisModal(true)}
      />
      <QuoteRequestModal
        isOpen={showDevisModal}
        onClose={() => setShowDevisModal(false)}
        agencyId={agency?.id}
        agencyName={agency?.name}
        primaryColor={primaryColor}
      />
      <Hero 
        agencyName={agency?.name || 'Agence'} 
        primaryColor={primaryColor}
        firstTitle={agency?.firstTitle || null}
        secondTitle={agency?.secondTitle || null}
        imageCover={agency?.imageCover ? getImageUrl(agency.imageCover) : null}
        textColor={heroTextColor}
      />
      {agency?.packages && agency.packages.length > 0 && (
        <PackageSection 
          packages={agency.packages}
          primaryColor={primaryColor}
        />
      )}
      {agency?.popularDestinations && agency.popularDestinations.length > 0 && (
        <PopularDestinations 
          destinations={agency.popularDestinations}
          primaryColor={primaryColor}
          onDevisClick={() => setShowDevisModal(true)}
        />
      )}
      <About 
        description={agency?.description || null}
        aboutImage={agency?.aboutImage ? getImageUrl(agency.aboutImage) : null}
        yearsExperience={agency?.yearsExperience || 0}
        expertise={agency?.expertise || null}
        satisfiedTravelers={agency?.satisfiedTravelers || 0}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
      <Prestations
        prestations={Array.isArray(agency?.prestations) ? agency.prestations : []}
        primaryColor={primaryColor}
      />
      {agency?.id ? (
        <ClientReservationSection agencyId={agency.id} primaryColor={primaryColor} />
      ) : null}
      <Contact 
        primaryColor={primaryColor}
        agencyId={agency?.id || 0}
        email={agency?.email || null}
        phone={agency?.phone || null}
        address={agency?.address || null}
      />
      <SocialLinks 
        facebook={agency?.facebook || null}
        instagram={agency?.instagram || null}
        tiktok={agency?.tiktok || null}
        primaryColor={primaryColor}
      />
      <WhatsAppButton phone={agency?.phone || null} />
    </div>
  );
}